import { useQuery } from '@tanstack/react-query'
import { Alert, Checkbox, Input, Modal, Select, Text } from '@viapeople/components'
import { AlertCircle, Info } from 'lucide-react'
import { useEffect } from 'react'
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form'
import { Link, useParams } from 'react-router-dom'
import { trpc } from '../../../../utils/trpc'
import { useCycleEmailConfigurationsStore } from '../../cycleEmailConfigurationsStore'
import type { CycleEmailConfiguration } from './types'

type EmailType = 'kickoff' | 'lastCall' | 'statusReport' | 'bulkReminder'

export const emailTypeOptions: { label: string; value: EmailType }[] = [
	{ label: 'Kickoff', value: 'kickoff' },
	{ label: 'Last Call', value: 'lastCall' },
	{ label: 'Status Report', value: 'statusReport' },
	{ label: 'Bulk Reminder', value: 'bulkReminder' },
]

const WEEK_DAYS = [
	{ label: 'M', value: 'mon' },
	{ label: 'T', value: 'tue' },
	{ label: 'W', value: 'wed' },
	{ label: 'T', value: 'thu' },
	{ label: 'F', value: 'fri' },
	{ label: 'S', value: 'sat' },
	{ label: 'S', value: 'sun' },
] as const

type WeekDay = (typeof WEEK_DAYS)[number]['value']

export type ListDisplay = CycleEmailConfiguration & {
	actions: React.ReactNode
	emailName: string
	emailType: string
}

type FormState = {
	emailName: string
	emailType: EmailType
	emailTemplateId: string
	daysBeforeDeadline: number
	sendEveryNDays: number
	stopAfterNEmails: number
	targetAudience: string[]
	schedule: string
	scheduleDayInterval: number
	scheduleWeekDay: WeekDay[]
}

const DEFAULT_FORM_STATE: FormState = {
	emailName: '',
	emailType: 'kickoff',
	emailTemplateId: '',
	daysBeforeDeadline: 0,
	sendEveryNDays: 1,
	stopAfterNEmails: 0,
	targetAudience: [],
	schedule: 'interval',
	scheduleDayInterval: 1,
	scheduleWeekDay: [],
}

export type CycleEmailModalState =
	| {
			mode: 'preview' | 'edit'
			item: ListDisplay
	  }
	| { mode: 'create' }

export const CycleEmailModal = ({
	onClose,
	modalState,
}: {
	onClose: () => void
	modalState: CycleEmailModalState | null
}) => {
	const { organizationId, cycleId } = useParams<{ organizationId: string; cycleId: string }>()

	const isEditable = modalState?.mode !== 'preview'

	const form = useForm<FormState>({
		defaultValues: DEFAULT_FORM_STATE,
	})

	useEffect(() => {
		if (modalState !== null) {
			let state = DEFAULT_FORM_STATE

			if (modalState.mode === 'edit' || modalState.mode === 'preview') {
				const config = modalState.item.configuration

				state = {
					emailName: config.emailName,
					emailType: config.emailType,
					emailTemplateId: modalState.item.emailTemplateId,
					daysBeforeDeadline: config.emailType === 'lastCall' ? config.daysBeforeDeadline : 0,
					sendEveryNDays: config.emailType === 'bulkReminder' ? config.sendEveryNDays : 1,
					stopAfterNEmails: config.emailType === 'bulkReminder' ? config.stopAfterNEmails : 0,
					targetAudience: config.emailType === 'statusReport' ? config.targetAudience : [],
					schedule: config.emailType === 'statusReport' ? config.schedule : 'interval',
					scheduleDayInterval:
						config.emailType === 'statusReport' && config.schedule === 'interval'
							? (config.scheduleDayInterval ?? 1)
							: 1,
					scheduleWeekDay:
						config.emailType === 'statusReport' && config.schedule === 'weekly' ? (config.scheduleWeekDay ?? []) : [],
				}
			}

			form.reset(state)
		}
	}, [form, modalState])

	const emailType = useWatch({ control: form.control, name: 'emailType' })
	const schedule = useWatch({ control: form.control, name: 'schedule' })

	const { data: templatesData, isLoading: isLoadingTemplates } = useQuery(
		trpc.admin.emailTemplates.search.queryOptions({
			organizationId: organizationId ?? '',
			page: 1,
			perPage: 100,
		}),
	)
	const templateOptions = (templatesData?.data ?? []).map((t) => ({ label: t.name, value: t.id }))

	const { useCreateConfiguration, useUpdateConfiguration } = useCycleEmailConfigurationsStore()
	const { createConfiguration, isCreating } = useCreateConfiguration()
	const { updateConfiguration, isUpdating } = useUpdateConfiguration()

	if (!cycleId || !organizationId) {
		return <Alert>Missing organization or cycle id</Alert>
	}

	const onSubmit = form.handleSubmit((data) => {
		const configuration = (() => {
			switch (data.emailType) {
				case 'kickoff':
					return { emailName: data.emailName, emailType: 'kickoff' as const }
				case 'lastCall':
					return {
						emailName: data.emailName,
						emailType: 'lastCall' as const,
						daysBeforeDeadline: data.daysBeforeDeadline,
					}
				case 'statusReport':
					return {
						emailName: data.emailName,
						emailType: 'statusReport' as const,
						targetAudience: data.targetAudience,
						schedule: data.schedule as 'interval' | 'weekly',
						...(data.schedule === 'weekly'
							? { scheduleWeekDay: data.scheduleWeekDay }
							: { scheduleDayInterval: data.scheduleDayInterval }),
					}
				case 'bulkReminder':
					return {
						emailName: data.emailName,
						emailType: 'bulkReminder' as const,
						sendEveryNDays: data.sendEveryNDays,
						stopAfterNEmails: data.stopAfterNEmails,
					}
			}
		})()

		if (modalState?.mode === 'edit') {
			updateConfiguration({
				id: modalState.item.id,
				cycleId: cycleId,
				emailTemplateId: data.emailTemplateId,
				configuration,
				onSuccess: onClose,
			})
		} else if (modalState?.mode === 'create') {
			createConfiguration({
				cycleId: cycleId,
				emailTemplateId: data.emailTemplateId,
				configuration,
				onSuccess: onClose,
			})
		}
	})

	return (
		<Modal
			isOpen={modalState !== null}
			onClose={onClose}
			fullContent
			title={
				modalState?.mode === 'create'
					? 'Configure New Email'
					: modalState?.mode === 'edit'
						? 'Edit Email'
						: 'Preview Email'
			}
			className='min-w-[800px]'
			buttons={
				modalState?.mode === 'preview'
					? [
							{
								children: 'Cancel',
								variant: 'secondary',
								onClick: onClose,
								disabled: isCreating || isUpdating,
							},
						]
					: [
							{
								children: 'Cancel',
								variant: 'secondary',
								onClick: onClose,
								disabled: isCreating || isUpdating,
							},
							{
								children: modalState?.mode === 'create' ? 'Create Email' : 'Save Changes',
								variant: 'primary',
								onClick: onSubmit,
								isLoading: modalState?.mode === 'create' ? isCreating : isUpdating,
							},
						]
			}>
			<FormProvider {...form}>
				<form onSubmit={() => {}}>
					<div className='p-8'>
						<div className='flex flex-col gap-8'>
							<Controller
								name='emailName'
								render={({ field: { value, onChange, ...field } }) => (
									<Input
										{...field}
										value={value ?? ''}
										label='Internal Email Name'
										placeholder='Insert the email name'
										onChange={onChange}
										disabled={!isEditable}
									/>
								)}
							/>

							<div className='flex flex-row gap-8'>
								<Controller
									name='emailType'
									render={({ field }) => (
										<Select
											{...field}
											label='Email Type'
											placeholder='Select the email type'
											className='basis-1/2'
											options={emailTypeOptions}
											disabled={!isEditable}
										/>
									)}
								/>

								<Controller
									name='emailTemplateId'
									rules={{ required: 'Email template is required' }}
									render={({ field, fieldState }) => (
										<div className='basis-1/2 flex flex-col gap-1'>
											<Select
												{...field}
												label='Email Template'
												placeholder={
													!isLoadingTemplates && templateOptions.length === 0
														? 'No templates available'
														: 'Select the email template'
												}
												options={templateOptions}
												disabled={!isEditable}
												variant={fieldState.error ? 'error' : 'default'}
												hint={fieldState.error?.message}
												searchable
											/>
											{!isLoadingTemplates && templateOptions.length === 0 && (
												<div className='flex items-center gap-1 text-sm text-red-600'>
													<AlertCircle className='w-4 h-4 shrink-0' />
													<span>
														<b>Missing Templates:</b> No templates found. Please go to{' '}
														<Link
															to={`/organizations/${organizationId}/email-templates`}
															className='underline'
															target='_blank'>
															Email Templates
														</Link>
														.
													</span>
												</div>
											)}
										</div>
									)}
								/>
							</div>

							{emailType === 'lastCall' && (
								<Controller
									name='daysBeforeDeadline'
									render={({ field: { value, onChange, ...field } }) => (
										<div>
											<label className='text-sm text-neutral-800 mb-1' htmlFor='daysBeforeDeadline'>
												Schedule
											</label>
											<div className='flex items-center'>
												Send{' '}
												<Input
													{...field}
													id='daysBeforeDeadline'
													value={value ?? ''}
													type='number'
													className='w-16'
													formClassName='w-16 m-1'
													onChange={(e) => onChange(Number(e.target.value))}
													disabled={!isEditable}
													min={0}
												/>{' '}
												day(s) before the cycle deadline
											</div>
											<Text variant='hint'>If set to 0, sends on the deadline day.</Text>
											<Alert variant='info' className='mt-4'>
												<span>
													<Info className='inline' /> <b>Requirement:</b> This e-mail will only be sent if a{' '}
													<b>Cycle Deadline</b> is configured in General Settings.
												</span>
											</Alert>
										</div>
									)}
								/>
							)}

							{emailType === 'statusReport' && (
								<div className='flex flex-col gap-6'>
									<Controller
										control={form.control}
										name='targetAudience'
										render={({ field }) => (
											<div>
												<span className='text-sm text-neutral-800 mb-2 block'>Target Audience</span>
												<div className='flex gap-6'>
													<Checkbox
														label='Cycle Admin'
														checked={field.value.includes('cycleAdmin')}
														disabled={!isEditable}
														onChange={(e) =>
															field.onChange(
																e.target.checked
																	? [...field.value, 'cycleAdmin']
																	: field.value.filter((v) => v !== 'cycleAdmin'),
															)
														}
													/>
													<Checkbox
														label='Direct Managers'
														checked={field.value.includes('directManagers')}
														disabled={!isEditable}
														onChange={(e) =>
															field.onChange(
																e.target.checked
																	? [...field.value, 'directManagers']
																	: field.value.filter((v) => v !== 'directManagers'),
															)
														}
													/>
												</div>
											</div>
										)}
									/>

									<div className='flex  items-end gap-4'>
										<div className='basis-1/4'>
											<Controller
												control={form.control}
												name='schedule'
												render={({ field }) => (
													<Select
														{...field}
														label='Schedule'
														options={[
															{ label: 'Interval', value: 'interval' },
															{ label: 'Weekly', value: 'weekly' },
														]}
														disabled={!isEditable}
													/>
												)}
											/>
										</div>

										{schedule === 'weekly' && (
											<Controller
												control={form.control}
												name='scheduleWeekDay'
												render={({ field }) => (
													<div className='flex gap-1 pb-1'>
														{WEEK_DAYS.map(({ label, value }) => (
															<button
																key={value}
																type='button'
																disabled={!isEditable}
																onClick={() => {
																	const current = field.value ?? []
																	field.onChange(
																		current.includes(value) ? current.filter((d) => d !== value) : [...current, value],
																	)
																}}
																className={`w-9 h-9 rounded-md text-sm font-medium transition-colors ${
																	field.value?.includes(value)
																		? 'bg-blue-600 text-white'
																		: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
																}`}>
																{label}
															</button>
														))}
													</div>
												)}
											/>
										)}

										{schedule === 'interval' && (
											<Controller
												control={form.control}
												name='scheduleDayInterval'
												render={({ field: { value, onChange, ...field } }) => (
													<div className='flex items-center'>
														Every{' '}
														<Input
															{...field}
															value={value ?? ''}
															type='number'
															onChange={(e) => onChange(Number(e.target.value))}
															className='w-16'
															formClassName='w-16 m-1'
															disabled={!isEditable}
															min={1}
														/>{' '}
														day(s).
													</div>
												)}
											/>
										)}
									</div>
								</div>
							)}

							{emailType === 'bulkReminder' && (
								<div className='flex flex-row gap-8'>
									<Controller
										name='sendEveryNDays'
										render={({ field: { value, onChange, ...field } }) => (
											<div className='basis-1/2'>
												<label className='text-sm text-neutral-800 mb-1' htmlFor='sendEveryNDays'>
													Schedule
												</label>

												<div className='flex items-center'>
													Every{' '}
													<Input
														{...field}
														id='sendEveryNDays'
														value={value ?? ''}
														type='number'
														className='w-16'
														formClassName='w-16 m-1'
														onChange={(e) => onChange(Number(e.target.value))}
														disabled={!isEditable}
														min={1}
													/>{' '}
													day(s).
												</div>
											</div>
										)}
									/>

									<Controller
										name='stopAfterNEmails'
										render={({ field: { value, onChange, ...field } }) => (
											<div className='basis-1/2'>
												<label className='text-sm text-neutral-800 mb-1' htmlFor='stopAfterNEmails'>
													Sending Limit
												</label>

												<div className='flex items-center'>
													Stop after{' '}
													<Input
														{...field}
														id='stopAfterNEmails'
														value={value ?? ''}
														type='number'
														className='w-16'
														formClassName='w-16 m-1'
														onChange={(e) => onChange(Number(e.target.value))}
														disabled={!isEditable}
														min={0}
													/>{' '}
													emails.
												</div>
											</div>
										)}
									/>
								</div>
							)}
						</div>
					</div>
				</form>
			</FormProvider>
		</Modal>
	)
}
